import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { FaCalendar, FaChevronLeft, FaChevronRight, FaList, FaInfoCircle } from 'react-icons/fa';
import { formatDateInTimezone } from '../../utils/dateUtils';

const easing = [0.22, 1, 0.36, 1];

// Strip HTML to plain text — DB descriptions can carry inline styles, nested
// elements, even malformed markup. On a tight card with line-clamp, raw HTML
// renders as overlapping glyphs / bleed-through. Plain text is the only safe path.
const toPlainText = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined' || !document?.createElement) {
    return String(html).replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

const EventsSection = ({
  events = [],
  eventsLoading = false,
  onEventClick
}) => {
  const [activeEventIndex, setActiveEventIndex] = useState(0);
  const [showAllEventsModal, setShowAllEventsModal] = useState(false);

  const getSortedEvents = () => {
    if (!events || !Array.isArray(events)) return [];
    const activeEvents = events.filter(event => event.isActive !== false);
    return [...activeEvents].sort((a, b) => {
      if (a.order && b.order) return a.order - b.order;
      if (a.order && !b.order) return -1;
      if (!a.order && b.order) return 1;
      return 0;
    });
  };

  const sortedEvents = getSortedEvents();
  const total = sortedEvents.length;

  // Clamp activeEventIndex when events change
  useEffect(() => {
    if (total > 0 && activeEventIndex >= total) {
      setActiveEventIndex(total - 1);
    }
  }, [total]);

  // Desktop: window of 3 centred on active event
  const getVisibleWindow = () => {
    if (total === 0) return [];
    if (total <= 3) return sortedEvents;
    let start = Math.max(0, activeEventIndex - 1);
    let end = start + 3;
    if (end > total) { end = total; start = Math.max(0, end - 3); }
    return sortedEvents.slice(start, end);
  };

  const visibleEvents = getVisibleWindow();
  const canGoPrevious = activeEventIndex > 0;
  const canGoNext = activeEventIndex < total - 1;

  const goToPrevious = () => { if (canGoPrevious) setActiveEventIndex(i => i - 1); };
  const goToNext    = () => { if (canGoNext)     setActiveEventIndex(i => i + 1); };

  const handleEventClick = (event) => { if (onEventClick) onEventClick(event); };

  // ── Shared sub-components — editorial stamps ───────────────────────
  const DateStamp = ({ event }) => {
    if (event.type === 'event' && event.date) {
      const day = formatDateInTimezone(event.date, event.timezone || 'UTC', { day: '2-digit' });
      const month = formatDateInTimezone(event.date, event.timezone || 'UTC', { month: 'short' });
      const time = formatDateInTimezone(event.date, event.timezone || 'UTC', { hour: '2-digit', minute: '2-digit', hour12: true });
      return (
        <div
          className="bg-background/95 backdrop-blur-sm px-2.5 py-1.5 leading-none text-foreground"
          style={{ borderRadius: '2px' }}
        >
          <div className="flex items-baseline gap-1">
            <span className="font-display font-bold text-base tabular-nums tracking-tight">{day}</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/70">{month}</span>
          </div>
          <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-foreground/55 mt-0.5 tabular-nums">{time}</div>
        </div>
      );
    }
    if (event.type === 'information') {
      return (
        <div
          className="bg-background/95 backdrop-blur-sm px-2 py-1 flex items-center gap-1.5"
          style={{ borderRadius: '2px' }}
        >
          <FaInfoCircle className="w-2.5 h-2.5 text-foreground/70" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/70">Info</span>
        </div>
      );
    }
    return null;
  };

  // Legacy alias for the "View All Events" modal references
  const TypeBadge = (props) => <DateStamp {...props} />;

  const EventThumbnail = ({ event, className = '' }) => (
    <div className={`relative bg-secondary overflow-hidden ${className}`}>
      {event.thumbnail?.url || event.thumbnail?.data ? (
        <img
          src={event.thumbnail.url || `data:${event.thumbnail.contentType};base64,${event.thumbnail.data}`}
          alt={event.title}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <FaCalendar className="w-7 h-7 text-primary/30" />
        </div>
      )}
    </div>
  );

  const activeEvent = sortedEvents[activeEventIndex];

  return (
    <>
      <div className="bg-background px-5 sm:px-10 py-8 sm:py-12">

        {/* Section header — editorial */}
        <div className="flex items-end justify-between mb-7 sm:mb-9 gap-4">
          <div>
            <div className="flex items-center gap-2.5 mb-2">
              <span className="inline-block h-px w-5 bg-primary" aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
                Calendar
              </span>
            </div>
            <h2
              className="font-display font-bold text-foreground tracking-tight leading-none"
              style={{ fontSize: 'clamp(20px, 2.6vw, 28px)', letterSpacing: '-0.02em' }}
            >
              What's Happening
            </h2>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowAllEventsModal(true)}
              className="sm:hidden text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/55 hover:text-primary transition-colors flex items-center gap-1.5"
            >
              <FaList className="w-3 h-3" />
              All
            </button>
            {total > 3 && (
              <div className="hidden sm:flex items-center gap-1">
                <button
                  onClick={goToPrevious}
                  disabled={!canGoPrevious}
                  className={`w-8 h-8 flex items-center justify-center transition-colors ${canGoPrevious ? 'text-foreground/70 hover:text-primary cursor-pointer' : 'text-foreground/20 cursor-not-allowed'}`}
                  aria-label="Previous"
                >
                  <FaChevronLeft className="w-3 h-3" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={!canGoNext}
                  className={`w-8 h-8 flex items-center justify-center transition-colors ${canGoNext ? 'text-foreground/70 hover:text-primary cursor-pointer' : 'text-foreground/20 cursor-not-allowed'}`}
                  aria-label="Next"
                >
                  <FaChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}
            {total > 0 && (
              <button
                onClick={() => setShowAllEventsModal(true)}
                className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/55 hover:text-primary transition-colors ml-2"
              >
                View all <span className="tabular-nums text-foreground/40">({total})</span>
              </button>
            )}
          </div>
        </div>

        {/* Mobile: horizontal scroll */}
        <div className="block sm:hidden">
          {eventsLoading ? (
            <div className="flex gap-4 overflow-hidden">
              {[0,1].map(i => (
                <div key={i} className="flex-shrink-0 w-[calc(70%-6px)] animate-pulse">
                  <div className="aspect-[4/5] bg-muted" style={{ borderRadius: '4px' }} />
                </div>
              ))}
            </div>
          ) : !total ? (
            <div className="py-10 pl-5 border-l-2 border-border">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-foreground/55 mb-1">No Events</div>
              <p className="text-sm text-foreground/70">Check back later for announcements and events.</p>
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-hide scroll-smooth">
              <div className="flex gap-4 pb-2">
                {sortedEvents.map((event) => (
                  <article
                    key={event._id}
                    onClick={() => handleEventClick(event)}
                    className="group flex-shrink-0 w-[calc(70%-6px)] cursor-pointer flex flex-col"
                  >
                    <div className="relative aspect-[4/5] overflow-hidden" style={{ borderRadius: '4px' }}>
                      <EventThumbnail event={event} className="w-full h-full" />
                      <div className="absolute top-2 right-2">
                        <DateStamp event={event} />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Desktop: 3-column grid */}
        <div className="hidden sm:block">
          {eventsLoading ? (
            <div className="grid grid-cols-3 gap-x-6 gap-y-8">
              {[0,1,2].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-muted" style={{ borderRadius: '4px' }} />
                </div>
              ))}
            </div>
          ) : !total ? (
            <div className="py-14 pl-6 border-l-2 border-border max-w-md">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-foreground/55 mb-1">No Upcoming Events</div>
              <p className="text-base text-foreground/70">Check back later for announcements and events.</p>
            </div>
          ) : (
            <motion.div
              className="grid grid-cols-3 gap-x-6 gap-y-8"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } }}
            >
              {visibleEvents.map((event) => (
                <motion.article
                  key={event._id}
                  onClick={() => handleEventClick(event)}
                  className="group cursor-pointer flex flex-col"
                  variants={{
                    hidden: { opacity: 0, y: 14 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: easing } }
                  }}
                  whileHover={{ y: -3, transition: { duration: 0.2, ease: easing } }}
                >
                  <div className="relative aspect-[4/5] overflow-hidden" style={{ borderRadius: '4px' }}>
                    <EventThumbnail event={event} className="w-full h-full transition-transform duration-700 group-hover:scale-[1.03]" />
                    <div className="absolute top-3 right-3">
                      <DateStamp event={event} />
                    </div>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}

          {/* Desktop step indicator — hash-mark instead of dots */}
          {total > 1 && (
            <div className="mt-6 flex items-end gap-[5px] h-3 max-w-xs">
              {sortedEvents.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveEventIndex(i)}
                  className="w-px hover:opacity-100 transition-all duration-200"
                  style={{
                    height: i === activeEventIndex ? '12px' : '6px',
                    backgroundColor: i === activeEventIndex ? 'hsl(var(--primary))' : 'hsl(var(--foreground) / 0.2)'
                  }}
                  aria-label={`Event ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── View All Modal ────────────────────────────────────────────── */}
      {showAllEventsModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAllEventsModal(false); }}
        >
          <div className="bg-background rounded-2xl shadow-xl w-full max-w-5xl flex flex-col max-h-[90vh] border border-border">

            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-foreground">All Events</h3>
                <p className="text-sm text-muted-foreground mt-0.5">{total} event{total !== 1 ? 's' : ''}</p>
              </div>
              <button
                onClick={() => setShowAllEventsModal(false)}
                className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground transition-colors text-lg"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
              {eventsLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[0,1,2,3,4,5].map(i => (
                    <div key={i} className="rounded-2xl border border-border overflow-hidden animate-pulse">
                      <div className="aspect-[4/3] bg-muted" />
                      <div className="p-4 flex flex-col gap-2">
                        <div className="h-4 bg-muted rounded-full w-3/4" />
                        <div className="h-3 bg-muted rounded-full w-full" />
                        <div className="h-3 bg-muted rounded-full w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : !total ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-3">
                    <FaCalendar className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-foreground">No upcoming events</p>
                  <p className="text-xs text-muted-foreground mt-1">Check back later for new announcements.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sortedEvents.map((event) => (
                    <div
                      key={event._id}
                      onClick={() => { handleEventClick(event); setShowAllEventsModal(false); }}
                      className="rounded-2xl border border-border overflow-hidden cursor-pointer group hover:border-primary hover:shadow-elevated transition-all duration-200 flex flex-col bg-card"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden flex-shrink-0">
                        <EventThumbnail event={event} className="w-full h-full group-hover:scale-105 transition-transform duration-300" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                        <div className="absolute top-3 right-3">
                          <TypeBadge event={event} size="md" />
                        </div>
                      </div>
                      <div className="p-4 flex flex-col flex-1 gap-2">
                        <h4 className="font-bold text-foreground text-sm leading-snug line-clamp-2">{event.title}</h4>
                        {event.description && (
                          <p className="text-xs text-muted-foreground line-clamp-3 flex-1">
                            {toPlainText(event.description)}
                          </p>
                        )}
                        {event.type === 'event' && event.date && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-auto pt-2 border-t border-border">
                            <FaCalendar className="w-3 h-3 text-primary flex-shrink-0" />
                            <span>
                              {formatDateInTimezone(event.date, event.timezone || 'UTC', {
                                weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit', hour12: true
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default EventsSection;
